
'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/doi-tac-module/lib/utils';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useAppTranslation } from '@/doi-tac-module/hooks/use-app-translation';
import { industriesByJobType, type Industry, allIndustries } from "@/doi-tac-module/lib/industry-data";
import type { SearchFilters } from '../job-search/search-results';
import { visaDetailsByVisaType } from '@/doi-tac-module/lib/visa-data';
import { getIndustryKeyByLaborType } from '@/doi-tac-module/lib/candidate-filter-utils';

type SearchModuleProps = {
    onSearch: (filters: Partial<SearchFilters>) => void;
    filters: Partial<SearchFilters>;
    onFilterChange: (newFilters: Partial<SearchFilters>) => void;
    showHero?: boolean;
}

export const AgencySearchModule = ({ onSearch, showHero = false, filters: initialFilters, onFilterChange: setParentFilters }: SearchModuleProps) => {
  const { t, language } = useAppTranslation();
  const [filters, setInternalFilters] = useState(initialFilters);
  const [availableIndustries, setAvailableIndustries] = useState<Industry[]>(allIndustries);
  const [isSearchExpanded, setIsSearchExpanded] = useState(showHero);

  useEffect(() => {
    setInternalFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    if (!showHero && window.innerWidth < 768) {
      setIsSearchExpanded(false);
    } else {
      setIsSearchExpanded(true);
    }
  }, [showHero]);

  useEffect(() => {
    const parentVisaSlug = getIndustryKeyByLaborType(filters.visa) || Object.keys(industriesByJobType).find(key => 
        (visaDetailsByVisaType[key as keyof typeof visaDetailsByVisaType] || []).some(detail => detail.slug === filters.visaDetail)
    );

    const industries = parentVisaSlug ? (industriesByJobType[parentVisaSlug as keyof typeof industriesByJobType] || allIndustries) : allIndustries;
    const uniqueIndustries = Array.from(new Map(industries.map(item => [item.name, item])).values());
    setAvailableIndustries(uniqueIndustries);
  }, [filters.visa, filters.visaDetail]);
  
  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    const newFilters = {...filters, [field]: value};
    setInternalFilters(newFilters);
    setParentFilters(newFilters);
  };

  const handleJobTypeChange = (value: string) => {
    const newVisa = value === 'all' ? '' : value;
    const newFilters: Partial<SearchFilters> = { visa: newVisa, visaDetail: '' };
    
    if (filters.industry && filters.industry !== 'all') {
        const visaKey = getIndustryKeyByLaborType(newVisa);
        const newIndustries = visaKey ? (industriesByJobType[visaKey] || allIndustries) : allIndustries;
        const isStillValid = newIndustries.some(ind => ind.name === filters.industry);
        if (!isStillValid) {
            newFilters.industry = '';
            newFilters.jobDetail = '';
        }
    }

    const updatedFilters = { ...filters, ...newFilters };
    setInternalFilters(updatedFilters);
    setParentFilters(updatedFilters);
  };
  
  const handleSearchClick = () => {
    onSearch(filters);
    if (!showHero && window.innerWidth < 768) {
      setIsSearchExpanded(false);
    }
  };

  const searchSummary = [
    (Object.values(visaDetailsByVisaType).flat().find(d => d.slug === filters.visaDetail))?.name,
    (allIndustries.find(i => i.slug === filters.industry))?.name,
    filters.q
  ].filter(Boolean).join(' / ');

  return (
    <section className={cn(
        "w-full bg-gradient-to-br from-[#FF6921] to-[#F2B92A] text-white transition-all duration-500",
        showHero ? "pt-20 md:pt-28 pb-10" : "py-4"
    )}>
        {showHero && (
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-6xl font-headline font-bold mb-4">
                    {t('hero_title')}
                </h1>
                 <p className="text-lg md:text-xl max-w-3xl mx-auto mb-10 text-white/90">
                 {t('hero_subtitle')}
                </p>
              </div>
            </div>
        )}
        <div className={cn(
            "container mx-auto px-4 md:px-6 relative z-10",
        )}>
            <Card className="max-w-6xl mx-auto shadow-2xl">
                 {!showHero && (
                    <div className="md:hidden p-2">
                        <Button 
                            variant="ghost" 
                            className="w-full justify-start text-left h-auto"
                            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                        >
                            <SlidersHorizontal className="h-5 w-5 mr-3 text-muted-foreground"/>
                            <div className="flex-grow min-w-0">
                                <p className="text-xs text-muted-foreground">{t('filtering_by')}</p>
                                <p className="font-semibold text-foreground truncate">{searchSummary || t('all')}</p>
                            </div>
                        </Button>
                    </div>
                )}
                
                <div className={cn(
                    "p-4 md:p-6",
                    isSearchExpanded ? "block" : "hidden md:block"
                )}>
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                        <div className="space-y-2 lg:col-span-1">
                            <Label htmlFor="search-type" className="text-foreground text-sm font-semibold">{t('labor_type')}</Label>
                            <Select onValueChange={(value) => handleJobTypeChange(value)} value={filters.visa || 'all'}>
                                <SelectTrigger id="search-type" className="h-11">
                                    <SelectValue placeholder={t('all_labor_types')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('all_labor_types')}</SelectItem>
                                    <SelectItem value="Lao động phổ thông">{t('skilled_labor')}</SelectItem>
                                    <SelectItem value="Lao động kỹ thuật">{t('technical_labor')}</SelectItem>
                                    <SelectItem value="Kỹ sư, trí thức">{t('professional')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 lg:col-span-1">
                            <Label htmlFor="search-industry" className="text-foreground text-sm font-semibold">{t('industry')}</Label>
                            <Select onValueChange={(value) => handleFilterChange('industry', value === 'all' ? '' : value)} value={filters.industry || 'all'}>
                                <SelectTrigger id="search-industry" className="h-11">
                                    <SelectValue placeholder={t('all_industries')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('all_industries')}</SelectItem>
                                    {availableIndustries.map((industry) => (
                                        <SelectItem key={industry.slug} value={industry.name}>
                                            {industry.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 lg:col-span-1">
                            <Label htmlFor="search-salary" className="text-foreground text-sm font-semibold">{t('desired_salary')}</Label>
                            <Select 
                                onValueChange={(value) => handleFilterChange('desiredSalaryVietnam', value === 'all' ? '' : value)} 
                                value={
                                    ['400', '500', '600', '700', '800', '900', '1000', '1200', '1500', '2000', '2500', '3000', '4000', '5000', '10000', '20000'].includes(filters.desiredSalaryVietnam || '')
                                        ? filters.desiredSalaryVietnam
                                        : 'all'
                                }
                            >
                                <SelectTrigger id="search-salary" className="h-11">
                                <SelectValue placeholder={t('all_salaries')} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    <SelectItem value="all">{t('all_salaries')}</SelectItem>
                                    {['400', '500', '600', '700', '800', '900', '1000', '1200', '1500', '2000', '2500', '3000', '4000', '5000', '10000', '20000'].map(val => (
                                        <SelectItem key={val} value={val}>{t('salary_under', { val: Number(val).toLocaleString() })}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                         <div className="space-y-2 lg:col-span-1">
                            <Button size="lg" className="w-full bg-[#FF6921] hover:bg-[#FF6921]/90 text-white text-lg h-10" onClick={handleSearchClick}>
                                <Search className="mr-2 h-5 w-5" /> {t('search')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    </section>
  );
}
